import { useState, useMemo } from 'react';

// Ventana fija de años que soporta el sistema: el SII solo fiscaliza los
// últimos 3 años y el piso de datos es enero 2024 (año comercial 2024 = AT
// 2025). No es relativa a la fecha de hoy — al agregar un año nuevo se
// amplía acá a mano (mismo criterio que el registry de F-22: nada se
// expande solo).
const MIN_YEAR = 2024;
const MAX_YEAR = 2026;

/**
 * Hook personalizado para manejar la selección de año
 * Diseñado para ser reutilizable y fácil de usar por agentes autónomos
 *
 * @param {number} defaultYear - Año por defecto (opcional, usa el año más reciente soportado si no se proporciona)
 * @param {number} yearsBack - Cantidad de años hacia atrás (acotado a MIN_YEAR)
 * @param {number} yearsForward - Cantidad de años hacia adelante (acotado a MAX_YEAR)
 * @returns {Object} { selectedYear, setSelectedYear, availableYears, getYearOptions }
 */
const useYearSelector = (defaultYear = null, yearsBack = 5, yearsForward = 1) => {
    const currentYear = Math.min(Math.max(new Date().getFullYear(), MIN_YEAR), MAX_YEAR);
    const initialYear = Math.min(Math.max(defaultYear || currentYear, MIN_YEAR), MAX_YEAR);

    const [selectedYear, setSelectedYear] = useState(initialYear);

    // Generar array de años disponibles
    const availableYears = useMemo(() => {
        const years = [];
        const startYear = Math.max(currentYear - yearsBack, MIN_YEAR);
        const endYear = Math.min(currentYear + yearsForward, MAX_YEAR);

        for (let year = endYear; year >= startYear; year--) {
            years.push(year);
        }

        return years;
    }, [currentYear, yearsBack, yearsForward]);

    // Obtener opciones formateadas para select
    const getYearOptions = useMemo(() => {
        return availableYears.map(year => ({
            value: year,
            label: year.toString(),
            isCurrentYear: year === currentYear
        }));
    }, [availableYears, currentYear]);

    return {
        selectedYear,
        setSelectedYear,
        availableYears,
        yearOptions: getYearOptions,
        currentYear
    };
};

export default useYearSelector;
